import { useEffect, useState } from 'react';
import './QuoteCopyModal.css';

import { Card, Button, Form, Row, Col } from 'react-bootstrap';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { MultiSelect } from '@grapecity/wijmo.react.input';
import { DataMap } from '@grapecity/wijmo.grid';
import { FlexGrid, FlexGridColumn, FlexGridCellTemplate } from '@grapecity/wijmo.react.grid';

import QuoteDetailModal from './QuoteDetailModal';
import AutoCompleteComponent from 'components/Input/AutoComplete';
import TextComponent from 'components/Input/TextComponent';

import { useAuth } from 'context/AuthContext';
import { useMaster } from 'context/MasterContext';
import { useRestApi } from 'context/RestApiContext';

function QuoteCopyModal({ show, setShow, onCopy }) {
  const auth = useAuth();
  const master = useMaster();
  const restApi = useRestApi();

  // 顧客検索条件
  const [searchCondition, setSearchCondition] = useState({
    SALES_TAN_CD: auth.auth.user_id,
    MITSU_DATE_START: null,
    MITSU_DATE_END: null,
    SYANAI_MEI: '',
    NAME: '',
    ADDRESS: '',
  });

  // 見積依頼台帳一覧
  const [quoteRequests, setQuoteRequests] = useState([]);

  // 顧客リスト（営業担当の変更時のみ更新）
  const [clientList, setClientList] = useState([]);

  // 初期化フラグ
  const [initialized, setInitialized] = useState(false);

  // 最新単価使用フラグ
  const [useLatestPrice, setUseLatestPrice] = useState(true);

  // 詳細モーダル
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  // 工事種類一覧（MultiSelect用）
  const [kojiSyuList, setKojiSyuList] = useState([]);

  // コード翻訳用のマッピング（空表示用の要素も追加）
  const planerWithEmpty = [...master.planer, { id: 0, name: '' }];
  const planerMap = new DataMap(planerWithEmpty, 'id', 'name');

  // 前回の営業担当コード（顧客リスト更新判定に利用）
  const [prevSalesTanCd, setPrevSalesTanCd] = useState(null);

  const handleChange = (prop) => (target) => {
    setSearchCondition((prev) => ({
      ...prev,
      [prop]: target?.value ?? target?.target?.value,
    }));
  };

  // 類似見積検索
  const searchQuote = (condition, shouldUpdateClientList = false) => {
    restApi.post('/api/quote/list', condition, (data) => {
      if (shouldUpdateClientList) {
        const newClientList = [
          { value: '' },
          ...(data.clientList || []).map((item) => ({ value: item.NAME_1 })),
        ];

        if (condition.NAME && !newClientList.some((x) => x.value === condition.NAME)) {
          newClientList.push({ value: condition.NAME });
        }

        setClientList(newClientList);
      }
      const _quoteRequests = data.baseQuotes.map((quoteRequest) => {
        return {
          ...quoteRequest,
          ADDRESS_ALL: `${quoteRequest.PREF == '群馬県' ? '' : quoteRequest.PREF}${quoteRequest.CITY
            }${quoteRequest.TOWN}${quoteRequest.ADDRESS}`,
        };
      });

      setQuoteRequests(_quoteRequests);
    });
  };

  // 検索ボタン
  const handleOnClickSearch = () => {
    const salesTanChanged = prevSalesTanCd !== searchCondition.SALES_TAN_CD;
    searchQuote(searchCondition, salesTanChanged);

    if (salesTanChanged) {
      setPrevSalesTanCd(searchCondition.SALES_TAN_CD);
    }
  };

  // 初回表示時のデータ取得
  useEffect(() => {
    if (!show || initialized) return;

    setKojiSyuList(master.kojiSyu.map((x) => x.name));

    const initialSearchCondition = { SALES_TAN_CD: auth.auth.user_id };
    restApi.post('/api/quote/list', initialSearchCondition, (data) => {


      const initialClientList = [
        { value: '' },
        ...(data.clientList || []).map((item) => ({ value: item.NAME_1 })),
      ];
      const _quoteRequests = data.baseQuotes.map((quoteRequest) => {
        return {
          ...quoteRequest,
          ADDRESS_ALL: `${quoteRequest.PREF == '群馬県' ? '' : quoteRequest.PREF}${quoteRequest.CITY
            }${quoteRequest.TOWN}${quoteRequest.ADDRESS}`,
        };
      });
      setQuoteRequests(_quoteRequests);
      setClientList(initialClientList);
    });

    setPrevSalesTanCd(auth.auth.user_id);
    setInitialized(true);
  }, [show, initialized, auth.auth.user_id, master.kojiSyu, restApi]);

  // 詳細表示
  const handleShowDetail = (quote) => {
    restApi.get(`/api/quote/${quote.QUOTE_NO}`, (data) => {
      setSelectedQuote({ ...quote, kbns: data.kbns, details: data.details });
      setShowDetailModal(true);
    });
  };

  // 一括コピー
  const handleCopyAll = (quoteRow) => {
    restApi.get(`/api/quote/${quoteRow.QUOTE_NO}`, (data) => {
      if (!data) {
        alert('見積情報の取得に失敗しました');
        return;
      }

      const doCopy = (details) => {
        const selectedData = {
          quote: quoteRow,
          headar: data.headar,
          kbns: data.kbns,
          details,
          client: data.client,
          constraction: data.constraction,
        };

        onCopy?.(quoteRow.QUOTE_NO, selectedData);
        handleClose();
      };

      // 最新単価を使用しない場合はそのままコピー
      if (!useLatestPrice) {
        doCopy(data.details);
        return;
      }

      // BASE_NO のリストを取得（重複除去）
      const baseNos = [
        ...new Set(
          data.details
            .filter((d) => d.BASE_NO && d.BASE_NO > 0)
            .map((d) => d.BASE_NO),
        ),
      ];

      // BASE_NO が無い場合はそのままコピー
      if (baseNos.length === 0) {
        doCopy(data.details);
        return;
      }

      // 最新単価を取得して明細へ反映
      restApi.post('/api/quote/latest-prices', { BASE_NOS: baseNos }, (priceMap) => {
        const updatedDetails = data.details.map((detail) => {
          const latest = detail.BASE_NO ? priceMap?.[String(detail.BASE_NO)] : null;
          if (!latest) return detail;

          const su = Number(detail.SU ?? 0);
          const tankA = Number(latest.TANKA ?? 0);
          const genkaTankA = Number(latest.GENKA_TANKA ?? 0);

          if (detail.KOUJI_TYPE === 3 && Number(detail.KOUJIHI_PER ?? 0) <= 100) {
            return {
              ...detail,
              TANKA: tankA,
              GENKA_RATE: Number(latest.GENKA_RATE ?? detail.GENKA_RATE ?? 0),
              GENKA_TANKA: genkaTankA,
            };
          }

          const kingaku = su * tankA;
          const genkaKingaku = su * genkaTankA;

          return {
            ...detail,
            TANKA: tankA,
            GENKA_RATE: Number(latest.GENKA_RATE ?? detail.GENKA_RATE ?? 0),
            GENKA_TANKA: genkaTankA,
            KINGAKU: kingaku,
            GENKA_KINGAKU: genkaKingaku,
          };
        });

        doCopy(updatedDetails);
      });
    });
  };

  const handleClose = () => {
    setShow(false);
    setSelectedQuote(null);
    setShowDetailModal(false);
  };

  // フォームsubmit処理
  const handleOnSubmitSearch = (e) => {
    e.preventDefault();
    handleOnClickSearch();
  };

  return (
    <>
      <Dialog
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '1600px',
            maxWidth: '95vw',
          },
        }}
        open={show && !showDetailModal}
        disableEnforceFocus
      >
        <DialogTitle className="p-3">見積書コピー</DialogTitle>

        <DialogContent className="px-0 py-0 pr-0 pl-0">
          <Card className="m-0">
            <Card.Body>
              <form onSubmit={handleOnSubmitSearch}>
                <Row>
                  <Col style={{ width: '155px', flex: '0 0 auto' }}>
                    <label className="item-header-label">営業担当</label>
                    <Form.Select
                      className="form-control"
                      value={searchCondition.SALES_TAN_CD}
                      onChange={handleChange('SALES_TAN_CD')}
                    >
                      {master.salesStaff.map((x, i) => (
                        <option key={i} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  <Col className="pl-1" style={{ width: '220px', flex: '0 0 auto' }}>
                    <Form.Group>
                      <label>顧客名</label>
                      <AutoCompleteComponent
                        displayMemberPath="value"
                        itemsSource={clientList}
                        value={searchCondition.NAME}
                        onChange={(inputElement, target) => {
                          handleChange('NAME')(target);
                        }}
                      />
                    </Form.Group>
                  </Col>

                  <Col className="pl-1" style={{ width: '220px', flex: '0 0 auto' }}>
                    <Form.Group>
                      <label>住所</label>
                      <TextComponent
                        placeholder="部分一致で検索します"
                        type="text"
                        value={searchCondition.ADDRESS}
                        onChange={handleChange('ADDRESS')}
                      />
                    </Form.Group>
                  </Col>

                  <Col className="pl-1" style={{ width: '220px', flex: '0 0 auto' }}>
                    <Form.Group>
                      <label>工事名（社内呼称）</label>
                      <TextComponent
                        placeholder="部分一致で検索します"
                        type="text"
                        value={searchCondition.SYANAI_MEI}
                        onChange={handleChange('SYANAI_MEI')}
                      />
                    </Form.Group>
                  </Col>

                  <Col className="pl-1" style={{ width: '360px', flex: '0 0 auto' }}>
                    <Form.Group>
                      <label className="item-header-label">見積日</label>
                      <div className="d-flex" style={{ width: '340px' }}>
                        <TextComponent
                          type="date"
                          value={searchCondition.MITSU_DATE_START ?? ''}
                          onChange={handleChange('MITSU_DATE_START')}
                        />
                        <span className="mt-2 ml-2 mr-2">～</span>
                        <TextComponent
                          type="date"
                          value={searchCondition.MITSU_DATE_END ?? ''}
                          onChange={handleChange('MITSU_DATE_END')}
                        />
                      </div>
                    </Form.Group>
                  </Col>

                  <Col className="pl-1" style={{ width: '260px', flex: '0 0 auto' }}>
                    <Form.Group>
                      <label>工事種別</label>
                      <MultiSelect
                        itemsSource={kojiSyuList}
                        checkedItems={searchCondition.KOUJI_SYU ?? []}
                        checkedItemsChanged={(sender) => {
                          setSearchCondition((prev) => ({
                            ...prev,
                            KOUJI_SYU: sender.checkedItems,
                          }));
                        }}
                      />
                    </Form.Group>
                  </Col>

                  <Col className="mt-1">
                    <Form.Group>
                      <br />
                      <Button variant="success" className="btn-fill" size="sm" type="submit">
                        検索
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
              </form>

              <Row className="mt-3">
                <Col>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>件数：{quoteRequests.length}件</div>

                    <label style={{ fontSize: '14pt', color: 'black' }}>
                      <input
                        className="mr-1"
                        style={{ transform: 'scale(1.5)' }}
                        type="checkbox"
                        checked={useLatestPrice}
                        onChange={(e) => setUseLatestPrice(e.target.checked)}
                      />
                      最新の単価でコピー
                    </label>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col>
                  <div className="table-scroll" style={{ height: 'calc(100vh - 400px)' }}>
                    <FlexGrid
                      itemsSource={quoteRequests}
                      selectionMode="Row"
                      headersVisibility="Column"
                      allowSorting={false}
                      loadedRows={(grid) => {
                        grid.rows.forEach((row) => {
                          row.height = 38;
                        });
                      }}
                    >
                      <FlexGridColumn
                        binding="SALES_TAN_CD"
                        header="営業担当"
                        width={100}
                        isReadOnly
                        dataMap={planerMap}
                      />
                      <FlexGridColumn binding="CLIENT_NAME" header="顧客名" width={250} isReadOnly />
                      <FlexGridColumn binding="ADDRESS_ALL" header="住所" width={300} isReadOnly />
                      <FlexGridColumn
                        binding="SYANAI_MEI"
                        header="工事名（社内呼称）"
                        width="*"
                        isReadOnly
                      />
                      <FlexGridColumn
                        binding="MITSU"
                        header="見積り金額(税抜)"
                        format="c0"
                        width={120}
                        isReadOnly
                      />
                      <FlexGridColumn binding="MITSU_DATE" header="見積日" width={100} isReadOnly />
                      <FlexGridColumn binding="KOUJI_SYU" header="工事種別" width={180} isReadOnly />

                      <FlexGridColumn header="" width={90} isReadOnly>
                        <FlexGridCellTemplate
                          cellType="Cell"
                          template={(cell) => (
                            <Button
                              variant="success"
                              size="sm"
                              className="btn-fill"
                              onClick={() => handleCopyAll(cell.item)}
                            >
                              一括コピー
                            </Button>
                          )}
                        />
                      </FlexGridColumn>

                      <FlexGridColumn header="" width={55} isReadOnly>
                        <FlexGridCellTemplate
                          cellType="Cell"
                          template={(cell) => (
                            <Button
                              variant="primary"
                              size="sm"
                              className="btn-fill"
                              onClick={() => handleShowDetail(cell.item)}
                            >
                              詳細
                            </Button>
                          )}
                        />
                      </FlexGridColumn>
                    </FlexGrid>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </DialogContent>

        <DialogActions className="p-3" style={{ justifyContent: 'space-between' }}>
          <Button variant="secondary" className="footer-button" onClick={handleClose}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 見積詳細モーダル */}
      <QuoteDetailModal
        show={showDetailModal}
        setShow={setShowDetailModal}
        quote={selectedQuote}
        onCopy={onCopy}
        onCloseParent={handleClose}
        useLatestPrice={useLatestPrice}
        setUseLatestPrice={setUseLatestPrice}
      />
    </>
  );
}

export default QuoteCopyModal;
